import React, { useEffect } from 'react'
import { graphql } from 'gatsby'
import Seo from '@components/Seo'
import { useStore } from '@Store/'
import { setHeaderColor, setShowHeaderBackground } from '@Store/'
import Hero from '@components/_Service/Hero'
import Information from '@components/_Service/Information'
import SubServices from '@components/_Service/SubServices'
import PageFooter from '@components/_Service/PageFooter'
import { getNamedColor } from '@utils'
import { colors } from '@styles/vars/colors.style'
import Builder from '@components/_Service/Builder'
import Experts from '@components/_Service/Experts'

const ServicePage = ({ data }) => {
  const [, dispatch] = useStore()
  const {
    seo,
    slug,
    heroBackgroundColour: heroBackgroundColourName,
    heroTextColour: heroTextColourName,
    title,
    heroTitle,
    featuredImage: heroImage,
    serviceInformation,
    subServices,
    experts,
    serviceContent,
    pageSeoTitle,
    footer,
  } = data.contentfulService
  const articles = data.allContentfulArticle.nodes
  const { insightsLink } = data.contentfulHome

  const heroBackgroundColour = getNamedColor(heroBackgroundColourName)
  const heroTextColour = getNamedColor(heroTextColourName)

  useEffect(() => {
    setHeaderColor(dispatch, heroTextColour, heroBackgroundColour)
    setShowHeaderBackground(dispatch, true);
  }, [dispatch, heroTextColour, heroBackgroundColour])

  return (
    <div>
      <Seo data={seo} slug={`what-we-do/${slug}`} />

      <Hero
        backgroundColor={heroBackgroundColour}
        textColor={heroTextColour}
        name={title}
        title={heroTitle}
        image={heroImage}
        pageSeoTitle={pageSeoTitle}
      />

      {serviceInformation && <Information data={serviceInformation}/>}

      {subServices && <SubServices subServices={subServices} backgroundColor={heroTextColour} />}

      {experts && <Experts expertItems={experts} backgroundColor={heroTextColour} />}

      <Builder 
          data={serviceContent} 
          articles={articles} 
          insightsLink={insightsLink} 
          backgroundColor={heroBackgroundColour} 
          textColor={heroTextColour}
          slug={slug}
      >
      </Builder>

      {footer && (
        <PageFooter content={footer} customBackground={heroTextColour} customText={colors.charcoal} />
      )}
    </div>
  )
}

export default ServicePage

export const pageQuery = graphql`query ServicePage($slug: String!, $tag: String!) {
  allContentfulArticle(
    sort: {date: DESC}
    filter: {metadata: {tags: {elemMatch: {contentful_id: {regex: $tag}}}}}
  ) {
    nodes {
      slug
      content {
        raw
      }
      formattedTitle {
        raw
      }
      metadata {
        tags {
          name
          contentful_id
        }
      }
      date(formatString: "MMMM YYYY")
      featuredImage {
        gatsbyImageData(height: 310, width: 310)
        description
      }
    }
  }
  contentfulHome {
    insightsLink {
      ... on Node {
        ...Link
      }
    }
  }
  contentfulService(slug: {eq: $slug}) {
    title
    slug
    seo {
      ...SEO
    }
    pageSeoTitle
    heroBackgroundColour
    heroTextColour
    featuredImage {
      gatsbyImageData(height: 600, width: 600)
      description
    }
    heroTitle {
      raw
    }
    serviceInformation {
      raw
    }
    experts {
      text {
        raw
      }
      subText {
        raw
      }
    }
    subServices {
      title
      slug
      parentService {
        slug
      }
    }
    serviceContent {
      ... on ContentfulServiceCaseStudies {
        __typename
        title
        background
        caseStudies {
          slug
          metadata {
            tags {
              name
            }
          }
          mainImage {
            gatsbyImageData(height: 420, width: 420)
            description
          }
          secondaryImage {
            gatsbyImageData(height: 420, width: 420)
            description
          }
          heroText {
            raw
          }
          title
        }
      }
      ... on ContentfulServiceChannels {
        __typename
        title
        subTitle
        background
        channelItems {
          title
          platform
          link
          image {
            gatsbyImageData(height: 700, width: 700)
            description
          }
        }
      }
      ... on ContentfulServiceGallery {
        __typename
        title
        background
        itemsStyle
        titleStyle
        items {
          ...Image
        }
      }
      ... on ContentfulServicePartners {
        __typename
        title
        subTitle
        background
        itemsStyle
        titleStyle
        useDarkLogos
        isClickable
        partnerItems {
          slug
          featuredImage {
            ...Image
          }
          lightImage {
            ...Image
          }
          darkImage {
            ...Image
          }
        }
      }
      ... on ContentfulLatestInsights {
        __typename
        title
      }
      ... on ContentfulTestimonySlides {
        __typename
        title
        quotes {
          label
          quote {
            quote
          }
          quoteContent {
            raw
          }
          image {
            ...Image
          }
          authorName
          authorDescription {
            authorDescription
          }
          link {
            ... on Node {
              ...Link
            }
          }
        }
      }
      ... on ContentfulServiceGrid {
        __typename
        title
        background
        gridLayout
        featuredTitle {
          raw
        }
        content {
          raw
        }
        items {
          text {
            raw
          }
          subText {
            raw
          }
          image {
            ...Image
          }
          url
        }
      }
      ... on ContentfulServiceSideBySide {
        __typename
        title
        featuredTitle {
          raw
        }
        content {
          raw
        }
        callToAction {
          raw
        }
        video {
          ...Video
        }
        buttonType
        backgroundColor
        textColor
      }
    }
    footer {
      ...PageFooter
    }
  }
}`
